import React from 'react';
import { Badge } from 'react-bootstrap';
import type { PublicationStatus } from '../../../models/ProductTypes';
import { StockStatus, SkinSeoulProductType, CpProductStatus } from '../../../models/ProductTypes';
import type { CMWorkflowStatus } from '../../../models/CatalogManagement';

type StatusType =
  | 'publication'
  | 'stock'
  | 'cpProductStatus'
  | 'productType'
  | 'wave'
  | 'brand'
  | 'cmWorkflow'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'secondary'
  | 'primary';

interface StatusBadgeProps {
  type: StatusType;
  value?: string | number | PublicationStatus | StockStatus | SkinSeoulProductType | CMWorkflowStatus;
  label?: string;
  size?: 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent) => void;
  title?: string;
  className?: string;
}

const getSizeStyles = (size: 'sm' | 'md' | 'lg') => {
  const sizes = {
    sm: { fontSize: '0.65rem', padding: '4px 8px' },
    md: { fontSize: '0.7rem', padding: '4px 8px' },
    lg: { fontSize: '0.75rem', padding: '6px 10px' },
  };
  return sizes[size];
};

const getPublicationStatusColor = (status: PublicationStatus): string => {
  switch (status) {
    case 'publish':
      return 'dark';
    case 'draft':
      return 'secondary';
    case 'pending':
      return 'info';
    case 'private':
      return 'secondary';
    default:
      return 'light';
  }
};

const getStockStatusColor = (status: StockStatus): string => {
  switch (status) {
    case StockStatus.IN_STOCK:
    case 'instock':
      return 'success';
    case StockStatus.OUT_OF_STOCK:
    case 'outofstock':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getCpProductStatusColor = (status: CpProductStatus | string): string => {
  switch (status) {
    case CpProductStatus.IN_STOCK:
    case 'instock':
      return 'success';
    case CpProductStatus.OUT_OF_STOCK:
    case 'outofstock':
      return 'danger';
    case CpProductStatus.OBSOLETE:
    case 'obsolete':
      return 'secondary';
    case CpProductStatus.ERROR:
    case 'error':
      return 'danger';
    default:
      return 'secondary';
  }
};

const getProductTypeColor = (type: SkinSeoulProductType | string): string => {
  switch (type) {
    case SkinSeoulProductType.Variable:
    case 'variable':
    case 'Variable':
      return 'primary';
    case SkinSeoulProductType.Variation:
    case 'variation':
    case 'Variation':
      return 'warning';
    case SkinSeoulProductType.Simple:
    case 'simple':
    case 'Simple':
      return 'info';
    default:
      return 'secondary';
  }
};

const getWaveColor = (wave: string | number) => {
  const colors: { [key: string]: string } = {
    manual: '#8E44AD',
    '1': '#E67E22',
    '2': '#9B59B6',
    '3': '#D35400',
    '4': '#A569BD',
    '5': '#D35400',
    '6': '#7D3C98',
    '7': '#AF7AC5',
  };
  const waveKey = String(wave);
  return {
    backgroundColor: colors[waveKey] || '#95A5A6',
    color: '#FFFFFF',
    border: 'none',
  };
};

export const getCMWorkflowStatusColor = (status: CMWorkflowStatus | string): string => {
  const statusMap: Record<string, string> = {
    PRODUCT_INFO_GENERATED: 'info',
    CP_OPT_RESTRUCTURED: 'info',
    IMG_PROCESSED: 'success',
    WP_UPLOADED: 'warning',
    CM_REQUESTED: 'primary',
    CM_APPROVED: 'success',
    CM_PUBLISHED: 'success',
  };
  return statusMap[status as string] || 'secondary';
};

const getStatusLabel = (type: StatusType, value: string | number | undefined, customLabel?: string): string => {
  if (customLabel) {
    return customLabel;
  }
  if (!value) {
    return '';
  }

  if (type === 'stock') {
    switch (value) {
      case StockStatus.OUT_OF_STOCK:
      case 'outofstock':
        return 'Out of Stock';
      case StockStatus.IN_STOCK:
      case 'instock':
        return 'In Stock';
      case StockStatus.ON_BACKORDER:
      case 'onbackorder':
        return 'On Backorder';
      default:
        return String(value);
    }
  }

  if (type === 'cpProductStatus') {
    switch (value) {
      case CpProductStatus.IN_STOCK:
      case 'instock':
        return 'In Stock';
      case CpProductStatus.OUT_OF_STOCK:
      case 'outofstock':
        return 'Out of Stock';
      case CpProductStatus.OBSOLETE:
      case 'obsolete':
        return 'Obsolete';
      case CpProductStatus.ERROR:
      case 'error':
        return 'Error';
      default:
        return String(value);
    }
  }

  if (type === 'productType') {
    switch (value) {
      case SkinSeoulProductType.Variable:
      case 'variable':
      case 'Variable':
        return 'Variable';
      case SkinSeoulProductType.Variation:
      case 'variation':
      case 'Variation':
        return 'Variation';
      case SkinSeoulProductType.Simple:
      case 'simple':
      case 'Simple':
        return 'Simple';
      default:
        return String(value);
    }
  }

  return String(value);
};

const getBadgeStyle = (type: StatusType, value: string | number | undefined, size: 'sm' | 'md' | 'lg') => {
  const sizeStyles = getSizeStyles(size);

  switch (type) {
    case 'wave':
      return { ...sizeStyles, ...getWaveColor(value || '') };
    case 'brand':
      return { ...sizeStyles, fontWeight: '600' as const };
    default:
      return sizeStyles;
  }
};

const getBadgeVariant = (type: StatusType, value: string | number | undefined): string => {
  switch (type) {
    case 'publication':
      return getPublicationStatusColor(value as PublicationStatus);
    case 'stock':
      return getStockStatusColor(value as StockStatus);
    case 'cpProductStatus':
      return getCpProductStatusColor((value as CpProductStatus) || '');
    case 'productType':
      return getProductTypeColor((value as SkinSeoulProductType) || '');
    case 'cmWorkflow':
      return getCMWorkflowStatusColor((value as CMWorkflowStatus) || '');
    case 'brand':
      return 'light';
    case 'info':
      return 'info';
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'danger':
      return 'danger';
    case 'secondary':
      return 'secondary';
    case 'primary':
      return 'primary';
    default:
      return 'secondary';
  }
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
  type,
  value,
  label: customLabel,
  size = 'md',
  onClick,
  title,
  className,
}) => {
  const badgeStyle = getBadgeStyle(type, value, size);
  const badgeVariant = getBadgeVariant(type, value);
  const label = getStatusLabel(type, value, customLabel);

  const badgeProps: any = {
    style: badgeStyle,
    bg: badgeVariant,
    onClick,
    title,
    className,
  };

  if (type === 'brand') {
    badgeProps.text = 'dark';
  }

  return <Badge {...badgeProps}>{label}</Badge>;
};
